import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { WizardActionsBar } from "@/components/wizard/WizardActionsBar";
import { q1Route, type AccidentWorkChoice } from "@/utils/financialFlow";
import { backFromFinancialQ1 } from "@/utils/intakeResume";
import { useTaskStore } from "@/store/task";
import { isInsuranceOnlyFlow } from "@/hooks/useWizardFlow";

/** Labels matching app-pwa language/en.ts */
const labels = {
  financialResponsibilityQuestion1:
    "Are any of your complaints the result of a car accident or injury at work?",
  /** Insurance-only task: the patient has not been asked about complaints. */
  financialResponsibilityQuestion1InsuranceOnly:
    "Is your visit the result of a car accident or an injury at work?",
  carAccident: "Car Accident",
  workInjury: "Work Injury",
  neither: "Neither",
  backButton: "Back",
} as const;

const OPTIONS: { value: AccidentWorkChoice; label: string }[] = [
  { value: "car-accident", label: labels.carAccident },
  { value: "work-injury", label: labels.workInjury },
  { value: "neither", label: labels.neither },
];

/**
 * First financial responsibility step: accident / work injury / neither.
 * Matches app-pwa FinancialResponsibility.vue.
 */
export function FinancialResponsibilityPage() {
  const navigate = useNavigate();
  const wizardSteps = useTaskStore((s) => s.wizardSteps);

  const goBack = () => navigate(backFromFinancialQ1(wizardSteps));

  // Always route through the insurance forms; the portal-config flag only affects
  // the final hand-off after the insurance section, not which branch shows here.
  const goToChoice = (value: AccidentWorkChoice) => navigate(q1Route(value));

  return (
    <div className="wizard-page w-full">
      <div className="wizard-page__content financial-responsibility-content flex justify-center px-4 py-6">
        <div className="complaint-history-card financial-step-card">
          <div className="text-center mb-6">
            <p className="financial-responsibility-question text-md font-medium text-gray-800 mb-0 font-weight-bold" style={{ fontSize: "30px" }}>
              {isInsuranceOnlyFlow(wizardSteps)
                ? labels.financialResponsibilityQuestion1InsuranceOnly
                : labels.financialResponsibilityQuestion1}
            </p>
          </div>
          <div className="flex justify-center">
            <div className="flex flex-wrap justify-evenly w-full gap-3 py-3">
              {OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className="choice-btn flex-1 min-w-[140px]"
                  onClick={() => goToChoice(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <WizardActionsBar>
        <Button type="button" variant="outline" onClick={goBack} aria-label={labels.backButton}>
          {labels.backButton}
        </Button>
      </WizardActionsBar>
    </div>
  );
}
